import type {FieldDescriptor} from './actionCardTypes';

const VALIDATION_KEY_PREFIX = 'forecast_agent.action_card.validation';
const isEmpty = (value: unknown): boolean =>
	value === null ||
	value === undefined ||
	value === '' ||
	(Array.isArray(value) && value.length === 0) ||
	// A cleared NumberField yields NaN (typeof NaN === 'number'); treat it as
	// empty so a required number can't be satisfied by NaN and NaN never reaches
	// the min/max comparisons (where `NaN < min` / `NaN > max` are both false).
	(typeof value === 'number' && Number.isNaN(value));

export const validateField = (descriptor: FieldDescriptor, value: unknown): string | null => {
	if (!descriptor.editable) return null;
	if (descriptor.required && isEmpty(value)) return `${VALIDATION_KEY_PREFIX}.required`;
	if (isEmpty(value)) return null;
	const constraints = descriptor.constraints ?? {};
	if (descriptor.type === 'number' && typeof value === 'number') {
		if (constraints.min !== undefined && value < constraints.min) return `${VALIDATION_KEY_PREFIX}.min`;
		if (constraints.max !== undefined && value > constraints.max) return `${VALIDATION_KEY_PREFIX}.max`;
	}
	if (descriptor.type === 'text' && typeof value === 'string') {
		if (constraints.maxLength !== undefined && value.length > constraints.maxLength)
			return `${VALIDATION_KEY_PREFIX}.max_length`;
		if (constraints.pattern) {
			try {
				if (!new RegExp(constraints.pattern).test(value)) return `${VALIDATION_KEY_PREFIX}.pattern`;
			} catch {
				// malformed pattern from wire data — skip constraint, treat as valid
			}
		}
	}
	if (descriptor.type === 'date' && typeof value === 'string') {
		if (constraints.minDate && value < constraints.minDate) return `${VALIDATION_KEY_PREFIX}.min_date`;
		if (constraints.maxDate && value > constraints.maxDate) return `${VALIDATION_KEY_PREFIX}.max_date`;
	}
	if (descriptor.type === 'daterange' && value && typeof value === 'object') {
		const {start, end} = value as {start?: string; end?: string};
		if (start && end && end < start) return `${VALIDATION_KEY_PREFIX}.date_order`;
		if (constraints.minDate && start && start < constraints.minDate) return `${VALIDATION_KEY_PREFIX}.min_date`;
		if (constraints.maxDate && end && end > constraints.maxDate) return `${VALIDATION_KEY_PREFIX}.max_date`;
	}
	return null;
};
